import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calculator, Clock, ListPlus, Plus, Camera, ChevronRight } from "lucide-react";
import { resolveImageUrl } from "@/lib/api";
import { useAuthedImage } from "./AuthImg";
import { matchSpecies, stripSpecies } from "@/lib/species";
import { fmtNum } from "@/lib/format";
import { ZoomableImage } from "./ZoomableImage";

const lumberDesc = (d) => { const sp = matchSpecies(d); const ex = stripSpecies(d); return sp && ex ? `${sp} - ${ex}` : (d || '').trim(); };

function formatTime(minutes) {
  if (minutes < 1) return `${Math.round(minutes * 60)} seconds`;
  if (minutes >= 60) {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hrs.toLocaleString()}h ${mins}m` : `${hrs.toLocaleString()}h`;
  }
  return `${minutes.toFixed(1)} min`;
}

function formatSkids(skids, boardsPerSkid) {
  if (!skids || skids <= 0) return null;
  const whole = Math.floor(skids);
  const remainder = skids - whole;
  const remainPcs = boardsPerSkid ? Math.round(remainder * boardsPerSkid * 100) / 100 : 0;
  if (whole > 0 && remainPcs > 0) return `${whole.toLocaleString()} Skid${whole > 1 ? 's' : ''} + ${remainPcs.toLocaleString()} Pcs.`;
  if (whole > 0) return `${whole.toLocaleString()} Skid${whole > 1 ? 's' : ''}`;
  if (remainPcs > 0) return `${remainPcs.toLocaleString()} Pcs.`;
  return `${Number(skids).toLocaleString()} Skids`;
}

function PriceInput({ value, onChange, label, testId }) {
  const display = `$${(value / 100).toFixed(2)}`;
  return (
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-[0.15em] text-zinc-400">{label}</p>
      <input
        type="text" inputMode="numeric" value={display}
        onKeyDown={(e) => {
          e.preventDefault();
          if (e.key >= '0' && e.key <= '9') onChange(prev => prev * 10 + parseInt(e.key));
          else if (e.key === 'Backspace') onChange(prev => Math.floor(prev / 10));
        }}
        onChange={() => {}}
        className="w-24 h-7 text-right text-sm font-bold border border-zinc-300 bg-white focus:border-orange-400 focus:outline-none px-2"
        style={{ fontFamily: "Chivo, sans-serif", caretColor: "transparent" }}
        data-testid={testId}
      />
    </div>
  );
}

function ExpandableComponent({ comp, idx, showPricing, hideAddToList, addComponentToList, pallet_quantity }) {
  const [expanded, setExpanded] = useState(false);
  const [compPriceRaw, setCompPriceRaw] = useState(0);
  const [compExtraCostRaw, setCompExtraCostRaw] = useState(0);

  const compBdFt = (comp.board_footage || 0) * comp.per_pallet * pallet_quantity;
  const compPrice = compPriceRaw > 0 ? compBdFt * (compPriceRaw / 100) : 0;
  const compExtraCost = compExtraCostRaw / 100;
  const compTotal = compPrice + compExtraCost;

  return (
    <div className="border-2 border-zinc-300 bg-white" data-testid={`result-card-${idx}`}>
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 bg-zinc-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <ChevronRight className={`w-4 h-4 text-zinc-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700 truncate" style={{ fontFamily: "Chivo, sans-serif" }}>
            {comp.name}
          </h4>
          {!expanded && (
            <span className="text-sm font-bold text-orange-600 ml-1 [overflow-wrap:anywhere]" data-testid={`result-total-${idx}`}>
              {comp.total.toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
      <div className="px-3 py-3">
        {/* Quantity */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Quantity</p>
            <p className="text-2xl font-bold text-orange-600 px-3 py-0.5 inline-block border border-dashed border-orange-300 max-w-full [overflow-wrap:anywhere]" style={{ fontFamily: "Chivo, sans-serif" }}>
              {comp.total.toLocaleString()}
            </p>
          </div>
          <span className="text-xs text-zinc-400 flex-shrink-0">{fmtNum(comp.per_pallet)}/pallet &times; {fmtNum(pallet_quantity)}</span>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-3 border-t border-zinc-100">
          {(comp.length || comp.height || comp.thickness) && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Size</p>
              <span className="text-xs font-semibold text-zinc-800">
                {comp.thickness || "—"} &times; {comp.height || "—"} &times; {comp.length || "—"}
              </span>
            </div>
          )}
          {comp.pcs_per_min > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Est. Time</p>
              <span className="text-xs font-semibold text-zinc-800">
                {formatTime(comp.total / comp.pcs_per_min)}
              </span>
            </div>
          )}
          {comp.board_footage > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Board Footage</p>
              <span className="text-xs font-semibold text-zinc-800 [overflow-wrap:anywhere]">{compBdFt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} bd ft</span>
            </div>
          )}
          {comp.skids != null && comp.skids > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Skids</p>
              {comp.boards_per_skid && <p className="text-[10px] text-zinc-400 mb-1">({comp.boards_per_skid} Per Skid)</p>}
              <span className="text-xs font-semibold text-zinc-800">{formatSkids(comp.skids, comp.boards_per_skid)}</span>
            </div>
          )}
          {comp.notched === true && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Notch</p>
              <span className="text-xs font-semibold text-zinc-800">
                {comp.notch_length ? `${comp.notch_length}"` : 'Yes'}
              </span>
            </div>
          )}
        </div>

        {/* Lumber */}
        {comp.lumber?.length > 0 && comp.lumber.some(l => l?.quantity || l?.pcs_per_cant) && (
          <div className="mt-3 pt-2 border-t border-zinc-100 text-violet-700">
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-2">Lumber</p>
            {(() => { let optNum = 0; return comp.lumber.filter(l => l?.quantity || l?.pcs_per_cant).map((l, li) => {
              optNum += 1;
              const cantInches = (l.length || 0) * 12;
              const compLength = comp.length || 0;
              // Match CutListItem exactly: user-entered Pcs/Cant wins, geometric is fallback
              const pcsLengthwise = compLength > 0 ? Math.floor(cantInches / compLength) : 0;
              const pcsPerCant = (l.pcs_per_cant || 0) || pcsLengthwise;
              const wasteInches = pcsLengthwise > 0 ? cantInches - (pcsLengthwise * compLength) : 0;
              return (
                <div key={li} className="p-2 border-2 border-violet-400 bg-violet-50/50 mb-1.5">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-violet-700 mb-0.5">Lumber Option {optNum}</p>
                  <span className="text-xs font-bold [overflow-wrap:anywhere]">
                    {fmtNum(Math.round((l.quantity || 0) * 100) / 100)} Cants - {l.thickness ? `${l.thickness}"` : ''}{l.width && l.width !== 'Random' ? ` x ${l.width}"` : l.width === 'Random' ? ' x Rndm' : ''} x {l.length}&apos;{l.description ? ` ${lumberDesc(l.description)}` : ''}
                    {pcsPerCant > 0 && <span className="text-[10px] font-normal text-violet-500 ml-1">({fmtNum(pcsPerCant)} pcs/cant)</span>}
                  </span>
                  {compLength > 0 && cantInches > 0 && (
                    <p className="text-[10px] text-violet-500 mt-0.5 ml-2">Waste: {wasteInches}&quot;</p>
                  )}
                </div>
              );
            }); })()}
          </div>
        )}

        {/* Extras */}
        {comp.extras && (
          <p className="text-xs text-yellow-700 mt-2 font-medium bg-yellow-50 px-2 py-1 border border-yellow-200">
            Extra&apos;s: {comp.extras}
          </p>
        )}

        {/* Per-component pricing (Office only) */}
        {showPricing && comp.board_footage > 0 && (
          <div className="mt-3 pt-3 border-t border-zinc-200 space-y-2">
            <PriceInput value={compPriceRaw} onChange={setCompPriceRaw} label="Price / Bd. Ft." testId={`comp-price-bdft-${idx}`} />
            <PriceInput value={compExtraCostRaw} onChange={setCompExtraCostRaw} label="Extra Cost" testId={`comp-extra-cost-${idx}`} />
            {compTotal > 0 && (
              <div className="flex items-center justify-between pt-1">
                <p className="text-xs uppercase tracking-[0.15em] font-medium text-zinc-500">Component Total</p>
                <p className="text-lg font-bold text-green-600" style={{ fontFamily: "Chivo, sans-serif" }}>
                  ${compTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Add to List — nails-named components never get added */}
        {!hideAddToList && !(comp.name || "").toLowerCase().includes("nail") && (
          <button
            onClick={() => addComponentToList(idx)}
            className="mt-3 w-full text-xs font-semibold text-blue-600 border border-blue-200 py-1.5 hover:bg-blue-50 transition-colors flex items-center justify-center gap-1"
            data-testid={`add-component-to-list-${idx}`}
          >
            <Plus className="w-3 h-3" />
            Add to Cut List
          </button>
        )}
      </div>
      )}
    </div>
  );
}

export function CalculationResults({ calculationResult, addToList, addComponentToList, showPricing, hideAddToList }) {
  const [showImage, setShowImage] = useState(false);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [overallPriceRaw, setOverallPriceRaw] = useState(0);
  const [overallExtraCostRaw, setOverallExtraCostRaw] = useState(0);

  const images = calculationResult?.images || (calculationResult?.image ? [calculationResult.image] : []);
  const lightboxSrc = useAuthedImage(showImage && images.length > 0 ? images[activeImageIdx] : null);

  return (
    <Card
      className="bg-white border border-zinc-200 rounded-none shadow-none md:col-span-2 lg:col-span-3"
      data-testid="results-card"
    >
      <CardHeader className="p-6 pb-4">
        <CardTitle
          className="text-xl font-bold text-zinc-900 tracking-tight"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Calculation Results
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 pt-0">
        {calculationResult ? (
          <div data-testid="calculation-results">
            <div className="mb-6 p-4 bg-zinc-900 text-white">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1">Materials for</p>
              <p className="text-2xl font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
                {calculationResult.pallet_quantity} &times; {calculationResult.pallet_type_name}
              </p>
            </div>

            {calculationResult.notes && (
              <div className="border-2 border-zinc-300 bg-zinc-50 p-4 mb-4" data-testid="result-notes-card">
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-2">Notes</p>
                <p className="text-sm text-zinc-700 whitespace-pre-wrap">{calculationResult.notes}</p>
              </div>
            )}

            {images.length > 0 && (
              <div
                className="border border-zinc-200 p-4 mb-4 hover:border-orange-400 transition-colors cursor-pointer flex items-center gap-2"
                onClick={() => { setActiveImageIdx(0); setShowImage(true); }}
                data-testid="result-photo-card"
              >
                <Camera className="w-5 h-5 text-zinc-400" />
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500">
                  {images.length > 1 ? `View Photos (${images.length})` : 'View Photo'}
                </p>
              </div>
            )}

            {/* Expandable components */}
            <div className="space-y-2 mb-6">
              {calculationResult.components.map((comp, idx) => (
                <ExpandableComponent
                  key={idx} comp={comp} idx={idx}
                  showPricing={showPricing} hideAddToList={hideAddToList}
                  addComponentToList={addComponentToList}
                  pallet_quantity={calculationResult.pallet_quantity}
                />
              ))}
            </div>

            {/* Totals */}
            <div className="border-t-2 border-zinc-900 pt-4">
              {calculationResult.components.some(c => c.pcs_per_min) && (() => {
                const missingTime = calculationResult.components.filter(c => !c.pcs_per_min && c.name.toLowerCase() !== 'nails').length;
                return (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-orange-600" />
                    <p className="text-xs uppercase tracking-[0.15em] font-medium text-zinc-500">Total Time</p>
                    {missingTime > 0 && <span className="text-xs font-bold text-red-500 bg-red-50 px-1.5 py-0.5">{missingTime} missing!</span>}
                  </div>
                  <p className="text-3xl font-bold text-orange-600" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="total-minutes-result">
                    {formatTime(calculationResult.components.reduce((sum, c) => sum + (c.pcs_per_min ? (c.total || 0) / c.pcs_per_min : 0), 0))}
                  </p>
                </div>
                );
              })()}
              {calculationResult.components.some(c => c.board_footage) && (() => {
                const missingBf = calculationResult.components.filter(c => !c.board_footage && c.name.toLowerCase() !== 'nails').length;
                const totalBdFt = calculationResult.components.reduce(
                  (sum, c) => sum + (c.board_footage || 0) * c.per_pallet * calculationResult.pallet_quantity, 0
                );
                return (
                <div className="pt-4 border-t border-zinc-200">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs uppercase tracking-[0.15em] font-medium text-zinc-500">Finished Product Board Feet</p>
                    {missingBf > 0 && <span className="text-xs font-bold text-red-500 bg-red-50 px-1.5 py-0.5">{missingBf} missing!</span>}
                  </div>
                  <p className="text-3xl font-bold text-orange-600 [overflow-wrap:anywhere]" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="total-board-footage-result">
                    {totalBdFt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                );
              })()}

              {/* Overall pricing (Office only) */}
              {showPricing && calculationResult.components.some(c => c.board_footage) && (() => {
                const totalBdFt = calculationResult.components.reduce(
                  (sum, c) => sum + (c.board_footage || 0) * c.per_pallet * calculationResult.pallet_quantity, 0
                );
                const overallPrice = overallPriceRaw > 0 ? totalBdFt * (overallPriceRaw / 100) : 0;
                const extraCost = overallExtraCostRaw / 100;
                const grandTotal = overallPrice + extraCost;
                return (
                <div className="pt-4 border-t border-zinc-200 space-y-2">
                  <PriceInput value={overallPriceRaw} onChange={setOverallPriceRaw} label="Price / Bd. Ft." testId="price-per-bdft-input" />
                  <PriceInput value={overallExtraCostRaw} onChange={setOverallExtraCostRaw} label="Extra Cost" testId="overall-extra-cost-input" />
                  {grandTotal > 0 && (
                    <div className="flex items-center justify-between pt-2">
                      <p className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500">Total Price</p>
                      <p className="text-4xl font-bold text-green-600" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="total-price-result">
                        ${grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  )}
                </div>
                );
              })()}

              {!hideAddToList && (
              <Button
                onClick={addToList}
                className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white rounded-none font-semibold tracking-wide h-12 text-base"
                data-testid="add-to-list-btn"
              >
                <ListPlus className="w-5 h-5 mr-2" />
                Add to Cut List
              </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-12" data-testid="no-calculation-results">
            <Calculator className="w-16 h-16 mx-auto mb-4 text-zinc-300" />
            <p className="text-zinc-500">Select a pallet type and enter quantity to calculate</p>
          </div>
        )}
      </CardContent>

      {showImage && images.length > 0 && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" style={{ pointerEvents: 'auto' }} onClick={() => setShowImage(false)} data-testid="image-lightbox">
          <div className="relative max-w-full max-h-[85vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
            <ZoomableImage src={lightboxSrc} alt={`${calculationResult.pallet_type_name} ${activeImageIdx + 1}`} className="max-w-full max-h-[75vh] object-contain" />
            {images.length > 1 && (
              <div className="flex items-center gap-4 mt-3">
                <button onClick={() => setActiveImageIdx(i => Math.max(0, i - 1))} disabled={activeImageIdx === 0}
                  className="px-3 py-1.5 bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 text-sm font-bold" data-testid="lightbox-prev">Prev</button>
                <span className="text-white text-sm font-bold">{activeImageIdx + 1} / {images.length}</span>
                <button onClick={() => setActiveImageIdx(i => Math.min(images.length - 1, i + 1))} disabled={activeImageIdx === images.length - 1}
                  className="px-3 py-1.5 bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 text-sm font-bold" data-testid="lightbox-next">Next</button>
              </div>
            )}
            <button onClick={() => setShowImage(false)} className="absolute top-2 right-2 p-2 bg-black/50 text-white hover:bg-black/70" data-testid="lightbox-close">
              <span className="text-lg font-bold">&times;</span>
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
